// These must be the first lines in src/index.js
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import React from 'react';
import ReactDOM from 'react-dom';
// import './css/index.css';
// import './css/global.css';

// import './css/main.css';
import './scss/main.scss';
import Router from './components';
import * as serviceWorker from './lib/serviceWorker';
import { Provider } from 'react-redux';
import configureStore from './redux/store';
import { BrowserRouter } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import history from './lib/history';
import { appPathname } from './lib/homeUrl';
import { registerStore as registerJourneyStore } from './services/journey';
import { registerUserTypeStore } from './lib/userTypeParam';
import { datadogRum } from '@datadog/browser-rum';
import { initAnalytics, RouteChangeTracker } from './analytics';

// OneMoney AA break-out. OneMoney's hosted consent page runs inside a full-viewport
// iframe on /dad/oneMoney; on completion the vendor redirects ITS OWN frame to our
// return URL (/dad/personalAndNominee). The iframe sandbox grants allow-top-navigation
// (see dadOneMoney), so if any journey page (/dad/*) ever finds itself rendered INSIDE
// an iframe, move the TOP window to that same URL here — before React mounts — so the
// top URL updates and the page is never left trapped in the frame. This replaces the
// old postMessage→history.push bridge / /onemoney-return landing. Scoped to /dad/* so
// the FAQ iframe (external T&C URL) is unaffected, and wrapped in try/catch since a
// cross-origin window.top access throws (i.e. it isn't our own OneMoney frame).
// NOTE: compare via appPathname(), NOT raw window.location.pathname — the app is
// served under a deploy prefix (/open-demat-account), so the raw pathname is
// '/open-demat-account/dad/...' and a '/dad' check against it is false. That
// silently disabled this break-out (the return page stayed trapped in the frame
// with the top URL stuck on /dad/oneMoney) rather than erroring.
try {
    if (window.self !== window.top
        && appPathname().toLowerCase().indexOf('/dad') === 0) {
        window.top.location.replace(window.location.href);
    }
} catch (e) { /* parent is cross-origin → not our OneMoney frame; ignore */ }

// Phase E - install payload encryption when the flag is on. The module is
// imported eagerly only in that case to avoid pulling Web Crypto / RSA-OAEP
// import code into the bundle when it isn't used.
if (process.env.REACT_APP_CRYPTO === 'on') {
    // eslint-disable-next-line global-require
    require('./services/crypto').installCrypto();
}

// Datadog RUM is temporarily disabled — set REACT_APP_DD_RUM=on (in the
// relevant .env file) to re-enable. Gated so no session/replay/trace data is
// collected until we deliberately turn it back on. Mirrors the
// REACT_APP_CRYPTO gate above.
if (process.env.REACT_APP_DD_RUM === 'on') {
    datadogRum.init({
        // Datadog browser-SDK keys are public-by-design (domain-allowlisted in the
        // vendor console, same model as a Stripe publishable key). They live in the
        // committed .env files alongside REACT_APP_TRUECALLER_APPKEY etc.
        applicationId: process.env.REACT_APP_DD_APPLICATION_ID,
        clientToken: process.env.REACT_APP_DD_CLIENT_TOKEN,
        // `site` refers to the Datadog site parameter of your organization
        // see https://docs.datadoghq.com/getting_started/site/
        site: 'datadoghq.com',
        service: process.env.REACT_APP_DD_SERVICE || 'ekyc3',
        env: process.env.REACT_APP_DD_ENV || 'uat',
        // Specify a version number to identify the deployed version of your application in Datadog
        version: process.env.REACT_APP_VERSION || '1.0.0',
        sessionSampleRate: 100,          // capture 100% of sessions
        sessionReplaySampleRate: 20,     // capture 20% of sessions with replay
        trackResources: true,            // Enable Resource tracking
        trackUserInteractions: true,     // Enable Action tracking
        trackLongTasks: true,            // Enable Long Tasks tracking
        trackBfcacheViews: true,
        // Enable distributed tracing to the backend API
        allowedTracingUrls: [process.env.REACT_APP_DD_TRACING_URL || 'https://ekycapi.dev.motilaloswal.cloud'],
        // Mask all captured text in Session Replay — this app handles PII
        // (PAN, Aadhaar, selfies, signatures), so never record it in the clear.
        defaultPrivacyLevel: 'mask',
        beforeSend: (event, context) => {
            if (event.type !== 'error') return;
            const message = (event.error && event.error.message) || '';
            const stack = (event.error && event.error.stack) || '';

            // discard a RUM error if its message includes 'Script error.'
            if (message.includes('Script error.')) {
                return false;
            }

            // Benign Google Identity Services (GIS) FedCM noise logged *inside*
            // Google's own gsi/client library — fires when the user dismisses the
            // FedCM card (AbortError) or a transient token fetch fails
            // (NetworkError). The email-verify flow falls back to the OAuth2 popup
            // (see src/hooks/useGoogleAuth.js), so these are not real failures and
            // we cannot catch them in our code (Google's [GSI_LOGGER] emits them).
            if (message.includes('[GSI_LOGGER]')
                && message.includes('FedCM get()')
                && (message.includes('AbortError') || message.includes('NetworkError'))) {
                return false;
            }

            // Errors thrown inside third-party tag-manager / ad scripts (GTM,
            // Google Ads, DoubleClick) that GTM injects into the page — not part of
            // our bundle, so unfixable from here. Match on the originating script
            // host only (never the message) so our own errors are never suppressed.
            const THIRD_PARTY_TAG_HOSTS = [
                'googletagmanager.com',
                'googleadservices.com',
                'google-analytics.com',
                'doubleclick.net',
                'googlesyndication.com',
            ];
            if (THIRD_PARTY_TAG_HOSTS.some((host) => stack.includes(host))) {
                return false;
            }
        },
        // NOTE: The @datadog/browser-rum-react plugin (reactPlugin / ErrorBoundary
        // / createBrowserRouter router tracking) is intentionally NOT wired up here.
        // That package requires React 18+ and react-router v6/v7; this app runs
        // React 16 with react-router-dom v5 + connected-react-router, so the plugin
        // is incompatible. RUM views/actions/resources are tracked without it.
    });
}

// Dev-only: silence the CRA runtime-error overlay. Compile errors still
// surface; this stops opaque cross-origin "Script error." events
// (third-party SDKs / analytics) and other runtime exceptions from
// hijacking the screen during local testing. Datadog filters Script errors
// separately in beforeSend above.
if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line global-require
    const { stopReportingRuntimeErrors } = require('react-error-overlay');
    // Defer: webpackHotDevClient calls startReportingRuntimeErrors after
    // user code runs. Calling stop before start throws "Not currently
    // listening", so schedule it for the next tick and swallow the throw
    // if the order ever flips.
    setTimeout(() => {
        try { stopReportingRuntimeErrors(); } catch (_) { /* not started yet or already stopped */ }
    }, 0);
}

// Let the reducers handle initial state
const initialState = {}
const store = configureStore(initialState)

// Wire the redux store into the services transport so it can dispatch
// JOURNEY_UPDATED / JWT_SET / SESSION_INITIALISED on every API response.
registerJourneyStore(store);
// Let lib/history's usertype URL decoration read user.applicationType from redux.
registerUserTypeStore(store);
initAnalytics({ store }); // MoEngage — fire-and-forget; SDK loads at idle time, mock mode without an App ID

// Dev-only: expose the store so manual probes / chrome-devtools MCP can
// dispatch actions and inspect state without relying on Redux DevTools.
if (process.env.REACT_APP_MODE === 'development' && typeof window !== 'undefined') {
    window.__STORE__ = store;
}

// Landing URL for the lead's attribution (leads.page_url, FOCTTA applicationString).
// Captured here, at full page load, because the registration page later drops the
// query string; a reload without one keeps the URL the customer first landed on.
try {
    if (window.location.search || !sessionStorage.getItem('landing_url')) {
        sessionStorage.setItem('landing_url', window.location.href);
    }
} catch (_) { /* storage blocked: signup simply goes without a pageUrl */ }

// Stage 0 (Arrival) — fire GET /journey/init once on cold start so the
// backend issues a session and the SPA stamps X-Session-Id on every
// subsequent call. Skipped if a sessionId is already present
// (refresh / resume in the same browser tab). Fire-and-forget: the
// transport persists the returned sessionId into sessionStorage and
// dispatches SESSION_INITIALISED before the user can plausibly submit
// any form, so subsequent calls find the header by the time they
// serialise. If the call fails the next API call surfaces the error
// with a missing X-Session-Id; we log a warning here for diagnostics.
if (!sessionStorage.getItem('sessionId')) {
    // eslint-disable-next-line global-require
    const JourneyApi = require('./services/journeyApi').default;
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'placement'].forEach((k) => {
        const v = params.get(k);
        if (v) utm[k] = v;
    });
    const referral = params.get('referral_code') || params.get('ref');
    if (referral) utm.referral_code = referral;
    if (typeof window !== 'undefined' && window.navigator) {
        const ua = window.navigator.userAgent || '';

        // Parse browser name from UA
        var browserName = 'Unknown';
        if (/OPR\/|Opera/.test(ua)) browserName = 'Opera';
        else if (/Edg\//.test(ua)) browserName = 'Edge';
        else if (/Chrome\//.test(ua) && !/Chromium\//.test(ua)) browserName = 'Chrome';
        else if (/Firefox\//.test(ua)) browserName = 'Firefox';
        else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browserName = 'Safari';
        utm.browser = browserName;

        // Parse OS name from UA
        var osName = 'Unknown';
        if (/Android/.test(ua)) osName = 'Android';
        else if (/iPhone|iPad|iPod/.test(ua)) osName = 'iOS';
        else if (/Windows/.test(ua)) osName = 'Windows';
        else if (/Mac OS X/.test(ua)) osName = 'macOS';
        else if (/Linux/.test(ua)) osName = 'Linux';
        utm.os_version = osName;

        // Detect network type via Network Information API (where supported)
        var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (conn && (conn.effectiveType || conn.type)) {
            utm.network_type = conn.effectiveType || conn.type;
        }
    }

    // Fire /journey/init — try geolocation first (5s timeout) so the backend
    // can resolve location_tag; if denied or timed out, proceed without coords.
    var fireInit = function() {
        new JourneyApi().init(utm).catch((err) => {
            // Don't break the SPA; first real call will report the missing session.
            // eslint-disable-next-line no-console
            console.warn('[ekyc] journey/init failed at boot; X-Session-Id will be missing on subsequent calls', err);
        });
    };

    // 4-state outcome. The previous version collapsed every error into one
    // branch, so a 5s TIMEOUT was counted as a refusal and inflated the
    // denial rate. err.code: 1=PERMISSION_DENIED 2=POSITION_UNAVAILABLE 3=TIMEOUT.
    // NOTE: browsers cannot distinguish "allow once" from "allow always" —
    // that is native-app only and is deliberately not modelled.
    var GEO_ERROR_STATE = { 1: 'denied', 2: 'unavailable', 3: 'timeout' };

    if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
            function(pos) {
                utm.latitude = pos.coords.latitude;
                utm.longitude = pos.coords.longitude;
                utm.location_permission = 'granted';
                fireInit();
            },
            function(err) {
                utm.location_permission =
                    (err && GEO_ERROR_STATE[err.code]) || 'unavailable';
                fireInit();
            },
            { enableHighAccuracy: false, timeout: 5000 }
        );
    } else {
        utm.location_permission = 'unavailable';
        fireInit();
    }
}

ReactDOM.render(
    <Provider store={store}>
        {/* <BrowserRouter> */}
        <ConnectedRouter history={history}>
            <Router />
            <RouteChangeTracker />
        </ConnectedRouter>
        {/* </BrowserRouter> */}
    </Provider>,
    document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();